import { blotterPreferenceStatePersistenceApiConnector } from 'apiConnector/blotters';
import { toBlotterPreferences } from 'epic/blotters/shared/preferenceState/preferenceStateEpics.helper';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import type { EpicWithThunk } from 'state/state.common';

import {
  secondaryMarketProductsPreferenceStateLoaded,
  secondaryMarketProductsPreferenceStateLoadingFailed,
  secondaryMarketProductsPreferenceStatePersisted,
  secondaryMarketProductsPreferenceStatePersistenceFailed,
} from '../../../../state/secondaryMarket/productSearch';

export function persistPreferenceStateEpic(
  aggregationFrequency: number = 1000,
  scheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    action$,
    state$,
    {
      httpClient,
      selectors: {
        fromSecondaryProductsSearchBlotter: { canSaveBlotterPreferences },
      },
    },
  ) =>
    action$.pipe(
      ofType('SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_PERSISTED'),
      withLatestFrom(state$),
      filter(([, state]) => canSaveBlotterPreferences(state)),
      debounceTime(aggregationFrequency, scheduler),
      mergeMap(([action]) => {
        return blotterPreferenceStatePersistenceApiConnector(httpClient)
          .saveState(action.blotterPreferenceState, 'secondaryMarketProducts')
          .pipe(
            mergeMap(_ => EMPTY),
            catchError(() => {
              return of(secondaryMarketProductsPreferenceStatePersistenceFailed());
            }),
          );
      }),
    );
}

export function loadPreferenceStateEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) => {
    return actions$.pipe(
      ofType('SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_GET'),
      mergeMap(() => {
        return blotterPreferenceStatePersistenceApiConnector(httpClient)
          .getState('secondaryMarketProducts')
          .pipe(
            map(response => secondaryMarketProductsPreferenceStateLoaded(toBlotterPreferences(response))),
            catchError(() => {
              return of(secondaryMarketProductsPreferenceStateLoadingFailed());
            }),
          );
      }),
    );
  };
}

export function columnStateChangedEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getSecondaryMarketProductsPreferenceState } }) =>
    action$.pipe(
      ofType('SECONDARY_MARKET_PRODUCT_COLUMN_STATE_CHANGED'),
      withLatestFrom(state$),
      mergeMap(([, state]) => {
        const blotterPreferenceState = getSecondaryMarketProductsPreferenceState(state);
        return of(secondaryMarketProductsPreferenceStatePersisted(blotterPreferenceState)).pipe(catchError(_ => EMPTY));
      }),
    );
}
